import { makeAutoObservable } from 'mobx';

import { mapApiToCatalogItem } from 'src/adapters';
import { doGet } from 'src/api';
import { ApiRoute } from 'src/constants';
import type {
  ICatalogItem,
  ICatalogItemApi,
  ICategories,
  ICategoryWithItems,
} from 'src/interfaces';

type LoadingState = {
  [key in ApiRoute]?: boolean;
};

class CatalogStore {
  init() {
    Promise.all([this.loadCategories(), this.loadCatalogItems()]).then(() =>
      this.setCategoriesWithItems()
    );
  }

  categories: ICategories = {};
  activeCategory: string | null = null;
  items: Record<string, ICatalogItem> = {};
  categoriesWithItems: ICategoryWithItems[] = [];
  loadingState: LoadingState = {};

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    this.init();
  }

  setLoadingState(type: ApiRoute, value: boolean) {
    this.loadingState[type] = value;
  }

  setCategories(categories: ICategories) {
    this.categories = categories;
  }

  setActiveCategory(category: string) {
    this.activeCategory = category;
  }

  setCatalogItems(items: ICatalogItemApi[]) {
    this.items = items.reduce(
      (items, item) => ({ ...items, [item.id]: mapApiToCatalogItem(item) }),
      {}
    );
  }

  setCategoriesWithItems() {
    this.categoriesWithItems = Object.entries(this.categories)
      .map(([categoryIndex, { title }]) => {
        const items = Object.values(this.items)
          .filter(({ category }) => categoryIndex === category)
          .sort((a, b) => a.score - b.score);
        return { category: categoryIndex, items, title };
      })
      .filter((category) => !!category.items.length);
  }

  async loadFromApi(type: ApiRoute) {
    this.setLoadingState(type, true);

    try {
      const { data } = await doGet(`catalog${type}`);
      return data;
    } catch (e) {
      console.log('e', e);
    } finally {
      this.setLoadingState(type, false);
    }
  }

  async loadCategories() {
    const categories = await this.loadFromApi(ApiRoute.CATEGORIES);
    this.setCategories(categories);
    this.setActiveCategory(Object.keys(categories)[0]);
  }

  async loadCatalogItems() {
    const items = await this.loadFromApi(ApiRoute.ITEMS);
    this.setCatalogItems(items);
  }

  getItemPrice(itemId: string) {
    return this.items[itemId]?.price ?? 0;
  }
}

export default new CatalogStore();
